import React, { Fragment, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { getAuth } from 'firebase/auth';
import { toast } from "react-toastify";
import { Eye, Pencil } from 'lucide-react';
import { MdDeleteOutline } from "react-icons/md";
import { Download, Plus } from 'lucide-react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import DataTable from './UI/DataTable';
import CommonForm from "./Commonform";
import PaginationControls from "./paginationcontrols";
import '../styles/index.css';
import PaginationSkeleton from "./Skeleton/PaginationSkeleton";
import BASE_URL from '../config';
import { useOrg } from "../contexts/OrgContext";
import Button from "@mui/material/Button";
import { usePermissions } from "../contexts/PermissionContext";



function PageTemplate({
    pageTitle,
    entityName,
    formFields,
    tableColumns,
    apiEndpoint,
    getDropdownOptions,
    formatInitialData,
    searchPlaceholder = "Search",
    readOnlyFieldsEdit = [],
    idField,
    nameField,
    filterFunction,
    showExportButton = false,
    users,
    selectedUsers = [],
    setSelectedUsers = () => { },
    showPagination = false,
    customSearchClass = "taskcategory-search-lower",
    onView,
    transformFetchedData = (data) => data,
    // New props for Firebase or custom handlers
    fetchData: externalFetchData,
    onSave: externalOnSave,
    onDelete: externalOnDelete,
    isSitePage = false,
    isTaskPage = false,
    isUserPage = false,
    isContractPage = false,
    isUserPermission = false,
    customCreateHandler,
    forceFormOpen = false,
    defaultFormData = {},
    disableInlineForm = false,
    onAdd,
    onEdit,
    section,
    permissionKey,
     refreshKey = 0,

}) {
    const { can, role, loading: permLoading } = usePermissions();
    const location = useLocation();
    // const isSitePage = location.pathname.includes('/sites'); // or match your exact path
    const { selectedOrgId } = useOrg();
    const [searchTerm, setSearchTerm] = useState("");
    const [data, setData] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState({});
    const [loading, setLoading] = useState(true);
    const isEditMode = !!editingItem;
    const [formKey, setFormKey] = useState(0); // Used to force remount
    const [formInitialData, setFormInitialData] = useState({}); // Control initialData


    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const startIndex = (currentPage - 1) * rowsPerPage;
    const filteredData = filterFunction
        ? filterFunction(data, searchTerm)
        : data.filter(item =>
            Object.values(item)
                .join(' ')
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );

    // Paginate the filtered data
    const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);


    // Load sidebar state from localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved ? JSON.parse(saved) : false;
    });


    const [selectedSite, setSelectedSite] = useState(null);

    const handleViewSite = (site) => {
        console.log("Selected site for view:", site);
        console.log("Site geometry:", site.geometry);
        setSelectedSite(site);
    };

    // Save sidebar state to localStorage
    const toggleSidebar = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
    };

    // useEffect(() => {
    //     fetchData();
    //     if (getDropdownOptions) {
    //         fetchDropdownOptions();
    //     }
    // }, [selectedOrgId]);

    useEffect(() => {
  fetchData();
}, [refreshKey]);


    useEffect(() => {
        if (!selectedOrgId) return;

        console.log("📌 Fetching for selectedOrgId:", selectedOrgId);
        fetchData();

        if (getDropdownOptions) {
            fetchDropdownOptions();
        }
    }, [selectedOrgId]);



    useEffect(() => {
        const updatedPolygon = localStorage.getItem('selectedPolygon');
        const updatedArea = localStorage.getItem('polygonArea');
        const isEditingPolygon = localStorage.getItem('editingPolygon') === 'true';
        const editingPolygonId = localStorage.getItem('editingPolygonId');

        if (updatedPolygon && updatedArea && isEditingPolygon && editingPolygonId) {
            // Find the existing item from data using the ID
            const existingItem = data.find(item => item.site_id === editingPolygonId);

            if (existingItem) {
                const updatedItem = {
                    ...existingItem,
                    geometry: JSON.parse(updatedPolygon),
                    area: parseFloat(updatedArea),
                };

                setEditingItem(updatedItem);
                setFormInitialData(updatedItem);
                setShowForm(true);
                setFormKey(prev => prev + 1); // Force form remount
                setViewMode(false);

                // Clean up polygon flags
                localStorage.removeItem('editingPolygon');
                localStorage.removeItem('editingPolygonId');
                localStorage.removeItem('selectedPolygon');
                localStorage.removeItem('polygonArea');
            }
        }
    }, [data]);


    useEffect(() => {
        if (forceFormOpen) {
            setFormInitialData(defaultFormData); // ✅ Directly use the passed form data
            setFormKey(prev => prev + 1); // Force remount
            setShowForm(true);
        }
    }, [forceFormOpen, defaultFormData]);

    // Inside your component
    useEffect(() => {
        const handleBeforeUnload = () => {
            localStorage.removeItem("selectedPolygon");
            localStorage.removeItem("polygonArea");
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);


    const fetchData = async () => {
        setLoading(true);
        try {
            let fetchedData = [];

            if (externalFetchData) {
                fetchedData = await externalFetchData();
            } else {
                const user = getAuth().currentUser;
                const token = await user.getIdToken();

                const response = await axios.get(`${BASE_URL}/api/${apiEndpoint}?organisation_id=${selectedOrgId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                fetchedData = response.data;
            }

            console.log("🚀 SLA Data:", fetchedData);


            // Sort by descending timestamp
            const sortedData = [...transformFetchedData(fetchedData)].sort((a, b) =>
                new Date(b.updated_at || b.created_at || b.timestamp) -
                new Date(a.updated_at || a.created_at || a.timestamp)
            );

            setData(sortedData);
        } catch (error) {
            console.error(`Error fetching ${entityName}:`, error);
            toast.error(
                error.response?.data?.error || `Error fetching ${entityName}:`,
                { position: "top-center", autoClose: 3000, toastId: `${entityName}-fetch-error` }
            );
        } finally {
            setLoading(false);
        }
    };



    const fetchDropdownOptions = async () => {
        try {
            const options = await getDropdownOptions();
            setDropdownOptions(options);
        } catch (error) {
            console.error("Error fetching dropdown options:", error);
        }
    };


    // Handle saving data
    const handleSave = async (formData) => {
        // 👉 Check for custom handler for CREATE mode (not edit)
        if (!editingItem && customCreateHandler) {
            try {
                await customCreateHandler(formData);
                toast.success(`${entityName} created successfully!`, {
                    position: "top-center",
                    autoClose: 3000,
                });
                handleCancel();
                await fetchData();
                return; // Stop here after custom create
            } catch (error) {
                console.error(`Error creating ${entityName}:`, error);
                toast.error(
                    error.response?.data?.error || `An error occurred while saving ${entityName}:`,
                    { position: "top-center", autoClose: 3000 }
                );
                return;
            }
        }



        // ✅ Fallback to default save logic (edit or other entities)
        if (externalOnSave) {
            try {
                await externalOnSave(formData, isEditMode, editingItem?.id);
                handleCancel();
                await fetchData();
            } catch (error) {
                console.error(`Error saving ${entityName}:`, error);
                toast.error(
                    error.response?.data?.error || `An error occurred while saving ${entityName}:`,
                    { position: "top-center", autoClose: 3000 }
                );
            }
        } else {
            try {
                const user = getAuth().currentUser;
                const token = await user.getIdToken();

                const method = editingItem ? 'put' : 'post';
                const url = editingItem
                    ? `${BASE_URL}/api/${apiEndpoint}/${editingItem[idField]}`
                    : `${BASE_URL}/api/${apiEndpoint}`;

                const dataToSend = (() => {
                    let transformed = formData;

                    // If SLA fields present, convert to INTERVAL string
                    if ('sla_hours' in formData || 'sla_minutes' in formData) {
                        transformed = {
                            ...formData,
                            sla_timing: `${Number(formData.sla_hours) || 0} hours ${Number(formData.sla_minutes) || 0} minutes`
                        };
                        delete transformed.sla_hours;
                        delete transformed.sla_minutes;
                    }

                    return {
                        ...transformed,
                        organisation_id: selectedOrgId
                    };
                })();


                console.log("Submitting formData with org:", dataToSend);

                await axios[method](url, dataToSend, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                toast.success(`${entityName} ${editingItem ? 'updated' : 'created'} successfully!`, {
                    position: "top-center",
                    autoClose: 3000,
                });

                handleCancel();
                await fetchData();
            } catch (error) {
                console.error(`Error saving ${entityName}:`, error);


                const errorMessage = error?.response?.data?.error || error.message;
                const statusCode = error?.response?.status;

                if (statusCode === 413 || errorMessage?.includes("File too large")) {
                    toast.error("File is too large. Max 70 KB allowed", {
                        position: "top-center",
                        autoClose: 3000,
                    });
                } else if (errorMessage?.includes("already exists")) {
                    toast.error(`${entityName} already exists!`, {
                        position: "top-center",
                        autoClose: 3000,
                    });
                } else {
                    toast.error(
                        error.response?.data?.error || `An error occurred while saving ${entityName}:`,
                        { position: "top-center", autoClose: 3000 }
                    );
                }
            }
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingItem(null);
        setFormInitialData({}); // 💥 Reset to empty
        setFormKey(prev => prev + 1); // 💥 Force remount
        setViewMode(false);

        localStorage.removeItem("selectedPolygon");
        localStorage.removeItem("polygonArea");
        localStorage.removeItem("siteFormData");
        localStorage.removeItem("fromMap");
    };


    const handleAddNew = () => {
        setEditingItem(null);
        localStorage.removeItem('fromMap');
        setFormInitialData({});
        setShowForm(true);
        setFormKey(prev => prev + 1);
        setViewMode(false);
    };


    const handleEdit = (item) => {
        const formattedItem = formatInitialData ? formatInitialData(item) : item;
        setEditingItem(formattedItem);
        setFormInitialData(formattedItem);
        setShowForm(true);
        setFormKey(prev => prev + 1);
        setViewMode(false);
    };

    const handleView = (item) => {
        const formattedItem = formatInitialData ? formatInitialData(item) : item;

        setEditingItem(formattedItem);
        setFormInitialData(formattedItem);
        setShowForm(true);
        setFormKey(prev => prev + 1);
        setViewMode(true);
    };


    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setIsDeleteModalOpen(true);
    };


    const handleExport = () => {
        if (entityName === "Users") {
            if (selectedUsers.length === 0) {
                toast.warning("No users selected");
                return;
            }

            const selectedUserData = data.filter(item => selectedUsers.includes(item[idField]));

            const csvRows = [
                ['Name', 'Email', 'Designation', 'Department', 'Phone', 'Job Title'],
                ...selectedUserData.map(user => [
                    user[nameField] || '',
                    user.email || '',
                    user.designation || 'N/A',
                    user.department || 'N/A',
                    user.phoneNumber || 'N/A',
                    user.jobTitle || 'N/A'
                ])
            ];

            const csvContent = "data:text/csv;charset=utf-8," +
                csvRows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${entityName.toLowerCase().replace(/\s+/g, "_")}_export.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // General export logic for other entities like Sites, Tasks, etc.
            if (filteredData.length === 0) {
                toast.warning(`No ${entityName.toLowerCase()} data to export`);
                return;
            }

            // Use field ids and labels from formFields
            const headers = formFields.map(field => field.label || field.id);
            const rows = filteredData.map(item =>
                formFields.map(field => {
                    const value = item[field.id];
                    if (typeof value === "string" || typeof value === "number") return value;
                    if (value instanceof Date) return value.toISOString();
                    return JSON.stringify(value ?? '');
                })
            );

            const csvRows = [headers, ...rows];
            const csvContent = "data:text/csv;charset=utf-8," +
                csvRows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${entityName.toLowerCase().replace(/\s+/g, "_")}_export.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };


    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        try {
            console.log("🔍 Deletion started for item:", itemToDelete);
            console.log("🏢 Selected Organisation ID:", selectedOrgId);

            if (externalOnDelete) {
                console.log("🛠️ Using external delete handler");
                await externalOnDelete(itemToDelete);
                // ✅ Let external handler manage its own toast (just like handleSave)
            } else {
                const user = getAuth().currentUser;
                const token = await user.getIdToken();
                console.log("✅ Firebase Auth Token acquired");

                const deleteUrl = `${BASE_URL}/api/${apiEndpoint}/${itemToDelete[idField]}?organisation_id=${selectedOrgId}`;
                console.log("📡 DELETE URL:", deleteUrl);

                await axios.delete(deleteUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                console.log(`🗑️ ${entityName} deleted successfully`);
                toast.success(`${entityName} deleted successfully`, {
                    position: "top-center",
                    autoClose: 3000,
                });
            }

            await fetchData(); // Refresh list after deletion
        } catch (error) {
            console.error(`❌ Error deleting ${entityName}:`, error);

            // ❌ Only handle errors if we're NOT using an external handler
            if (!externalOnDelete) {
                if (error.response?.data?.error) {
                    toast.error(error.response.data.error, {
                        position: "top-center",
                        autoClose: 4000,
                    });
                } else {
                    toast.error(`Failed to delete ${entityName}`, {
                        position: "top-center",
                        autoClose: 3000,
                    });
                }
            }
        }

        setIsDeleteModalOpen(false);
        setItemToDelete(null);
    };



    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
    };

    // Modern color palette (Material Design)
    const blue = '#6C8EBF';
    const white = '#ffffff';
    const gray = '#9e9e9e';
    const border = '#e0e0e0';

    return (
        <Fragment>
            <div style={{ padding: '20px' }}>
                    <div className="service">
                        <div className="header3">
                            {/* Show Global Search Bar Except in Site Page & View Mode */}
                            {!(isSitePage && viewMode) && !(isTaskPage && showForm) && !(isUserPage && showForm) && !(isContractPage && showForm) && (
                                <div className={`search-container-contractor ${showForm ? customSearchClass || 'search-with-form' : ''}`}>
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Task Category Page - Show only when form is open */}
                            {isTaskPage && showForm && (
                                <div className="taskcategory-search-container">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Show Special Search Bar Only in Site Page & View Mode */}
                            {isSitePage && viewMode && (
                                <div className="special-search-bar">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}

                            {isUserPage && showForm && (
                                <div className="special-search-bar-user">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}


                            {isContractPage && showForm && (
                                <div className="special-search-bar-contract">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>


                        <div className="content">
                            <div className="content-header">
                                <h1>{pageTitle}</h1>
                                    {!showForm && isUserPermission && can(section, permissionKey, "create") && (
                                    <header>
                                        <Button
                                            className="add-btn-tree"
                                            variant="contained"
                                            // onClick={handleAddNew}
                                            onClick={onAdd ? onAdd : handleAddNew}
                                            startIcon={<Plus size={14} />}
                                            sx={{
                                                backgroundColor: blue,  // example blue
                                                color: white,
                                                fontSize: "12px",            // 👈 text size here
                                                padding: "6px 16px",
                                                borderRadius: "8px",
                                                textTransform: "none",       // prevent ALL CAPS
                                                "&:hover": {
                                                    backgroundColor: blue,
                                                },
                                                "&.Mui-disabled": {
                                                    backgroundColor: border,
                                                    color: gray,
                                                },
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </header>
                                )}

{!showForm && can(section, permissionKey, "create") && (
                                    <header>
                                        <Button
                                            className="add-btn-tree"
                                            variant="contained"
                                            onClick={onAdd ? onAdd : handleAddNew}
                                            startIcon={<Plus size={14} />}
                                            sx={{
                                                backgroundColor: blue,  // example blue
                                                color: white,
                                                fontSize: "12px",            // 👈 text size here
                                                padding: "6px 16px",
                                                borderRadius: "8px",
                                                textTransform: "none",       // prevent ALL CAPS
                                                "&:hover": {
                                                    backgroundColor: blue,
                                                },
                                                "&.Mui-disabled": {
                                                    backgroundColor: border,
                                                    color: gray,
                                                },
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </header>
                                )}

{showExportButton && !showForm && can(section, permissionKey, "read") && (
                                    <button
                                        className={`export-btn-user ${isCollapsed ? 'expand' : 'shrink'}`}
                                        onClick={handleExport}
                                    >
                                        <Download size={14} />
                                        Export
                                    </button>
                                )}

                            </div>

                            {!disableInlineForm && showForm && (
                                <CommonForm
                                    key={formKey}
                                    isOpen={showForm}
                                    onClose={() => {
                                        handleCancel();
                                        setViewMode(false);
                                    }}
                                    onSave={handleSave}
                                    initialData={formInitialData}
                                    fields={formFields}
                                    dropdownOptions={dropdownOptions}
                                    readOnlyFields={viewMode
                                        ? formFields.map(f => f.id)
                                        : (editingItem ? readOnlyFieldsEdit : [])}
                                    title={viewMode
                                        ? `View ${entityName}`
                                        : (editingItem ? `Edit ${entityName}` : `Add ${entityName}`)}
                                    isEditMode={isEditMode}
                                    viewOnly={viewMode}
                                    geojson={viewMode ? editingItem?.geometry : null}
                                    showAddPolygonButton={isSitePage && !viewMode && !isEditMode}
                                    showEditPolygonButton={isSitePage && !viewMode && isEditMode}
                                    showCloseButton={viewMode && isSitePage}
                                    showCancelButton={!isSitePage}
                                    showCancelButtonSitePage={isSitePage && !viewMode}
                                />
                            )}

                        </div>


                        <div className={`service-table ${isCollapsed ? 'collapsed-sidebar' : 'expanded-sidebar'} ${showForm ? 'form-open' : ''}`}>
                            {/* ✅ Only show table if user has read permission */}
                            {can(section, permissionKey, "read") ? (
                                <>
                                    <DataTable
                                        columns={tableColumns}
                                        data={currentData}
                                        loading={loading}
                                        renderActions={(item) => (
                                            <div className="actions-2">
{can(section, permissionKey, "update") && !isUserPermission && (
                                                    <button className="action-button-1" onClick={() => handleEdit(item)}>
                                                        <Pencil size={13.5} />
                                                    </button>
                                                )}
                                                    {can(section, permissionKey, "update") && isUserPermission && (
                                                    <button
                                                        className="action-button-1"
                                                        onClick={() => onEdit(item)}  // parent’s handler
                                                    >
                                                        <Pencil size={13.5} />
                                                    </button>
                                                )}
{can(section, permissionKey, "read") && !isUserPermission && (
                                                    <button className="action-button-1" onClick={() => handleView(item)}>
                                                        <Eye size={13.5} />
                                                    </button>
                                                )}
                                                {can(section, permissionKey, "delete") && isUserPermission && (
                                                    <button
                                                        className="action-button-1"
                                                        onClick={() => handleDeleteClick(item)}
                                                        style={{ color: '#D2726A' }}
                                                    >
                                                        <MdDeleteOutline size={13.5} />
                                                    </button>
                                                )}
{can(section, permissionKey, "delete") && !isUserPermission && (
                                                    <button
                                                        className="action-button-1"
                                                        onClick={() => handleDeleteClick(item)}
                                                        style={{ color: '#D2726A' }}
                                                    >
                                                        <MdDeleteOutline size={13.5} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {showPagination && (
                                        loading ? (
                                            <PaginationSkeleton />
                                        ) : (
                                            <PaginationControls
                                                totalItems={data.length}
                                                rowsPerPage={rowsPerPage}
                                                currentPage={currentPage}
                                                onPageChange={setCurrentPage}
                                                onRowsPerPageChange={setRowsPerPage}
                                                isCollapsed={isCollapsed}
                                            />
                                        )
                                    )}
                                </>
                            ) : (
                                // Optional fallback message
                                <div className="no-permission-message">
                                    You don’t have permission to view {entityName} data.
                                </div>
                            )}
                        </div>


                        <DeleteConfirmationModal
                            isOpen={isDeleteModalOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            userName={itemToDelete && nameField ? itemToDelete[nameField] : ''}
                        />
                    </div>
            </div>
        </Fragment>
    );
}

export default PageTemplate;